import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
} from "react";
import { IconCheck, IconWarning, IconInfo, IconX, IconInbox } from "./icons.jsx";

/* ---------------- toasts ---------------- */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((current) => [...current, { ...toast, id }]);
      // Errors stay until dismissed. Auto-hiding a failure is how an operator
      // ends up believing an action succeeded.
      if (toast.kind !== "error") {
        setTimeout(() => dismiss(id), 4500);
      }
      return id;
    },
    [dismiss]
  );

  const value = {
    success: (title, body) => push({ kind: "success", title, body }),
    error: (title, body) => push({ kind: "error", title, body }),
    info: (title, body) => push({ kind: "info", title, body }),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            {t.kind === "success" && <IconCheck />}
            {t.kind === "error" && <IconWarning />}
            {t.kind === "info" && <IconInfo />}
            <div style={{ flex: 1 }}>
              <div className="toast-title">{t.title}</div>
              {t.body && <div className="toast-body">{t.body}</div>}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <IconX />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside a ToastProvider");
  return ctx;
}

/* ---------------- primitives ---------------- */

export function Card({ title, hint, actions, children, footer, style }) {
  return (
    <section className="card" style={style}>
      {(title || actions) && (
        <header className="card-header">
          <div>
            {title && <h2>{title}</h2>}
            {hint && <p className="hint">{hint}</p>}
          </div>
          {actions && <div className="topbar-actions">{actions}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
      {footer && <div className="card-footer">{footer}</div>}
    </section>
  );
}

export function Stat({ label, value, sub, icon: Icon }) {
  return (
    <div className="stat">
      <div className="stat-label">
        {Icon && <Icon />}
        {label}
      </div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

/**
 * PageIntro explains what a screen does before the operator uses it.
 *
 * These screens are used rarely and often under pressure. One sentence of
 * orientation prevents the class of mistake that is only discovered later in
 * the field, which is far more expensive than the space it costs here.
 */
export function PageIntro({ title, children }) {
  return (
    <div className="page-intro">
      <IconInfo />
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export function Callout({ kind = "info", children }) {
  const Icon = kind === "info" ? IconInfo : IconWarning;
  return (
    <div className={`callout callout-${kind}`}>
      <Icon />
      <div>{children}</div>
    </div>
  );
}

/**
 * Field wraps a form control with its label, hint and error text.
 *
 * The label is tied to the control programmatically rather than by proximity,
 * so a screen reader announces the right name, and clicking the label focuses
 * the control. The hint and error are attached with aria-describedby so they
 * are read out too instead of being decoration only a sighted user benefits
 * from.
 */
export function Field({ label, required, hint, error, children }) {
  const generatedId = useId();
  const control = isValidElement(children) ? children : null;
  const controlId = control?.props?.id || generatedId;
  const messageId = `${controlId}-message`;
  const message = error || hint;

  const described = [control?.props?.["aria-describedby"], message ? messageId : null]
    .filter(Boolean)
    .join(" ");

  const labelled =
    control &&
    cloneElement(control, {
      id: controlId,
      "aria-describedby": described || undefined,
      "aria-invalid": error ? true : control.props["aria-invalid"],
      "aria-required": required ? true : control.props["aria-required"],
    });

  return (
    <div className="field">
      {label && (
        <label htmlFor={controlId}>
          {label}
          {required && (
            <span className="required" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {labelled ?? children}
      {error ? (
        <div className="field-error" id={messageId} role="alert">
          <IconWarning width={13} height={13} />
          {error}
        </div>
      ) : (
        hint && (
          <div className="hint" id={messageId}>
            {hint}
          </div>
        )
      )}
    </div>
  );
}

export function Badge({ kind = "neutral", plain, children }) {
  return <span className={`badge badge-${kind}${plain ? " badge-plain" : ""}`}>{children}</span>;
}

/** Maps a device lifecycle state onto a badge colour. */
export function StateBadge({ state }) {
  const map = {
    MANUFACTURED: "neutral",
    ISSUED: "info",
    ACTIVE: "success",
    SUSPENDED: "warning",
    DECOMMISSIONED: "danger",
  };
  return <Badge kind={map[state] ?? "neutral"}>{titleCase(state)}</Badge>;
}

export function TransactionBadge({ state }) {
  const map = {
    PENDING: "neutral",
    DISPATCHED: "info",
    ACKNOWLEDGED: "brand",
    COMPLETED: "success",
    FAILED: "danger",
    EXPIRED: "warning",
  };
  return <Badge kind={map[state] ?? "neutral"}>{titleCase(state)}</Badge>;
}

export function SeverityBadge({ severity }) {
  const map = { INFO: "info", WARNING: "warning", CRITICAL: "danger" };
  return <Badge kind={map[severity] ?? "neutral"}>{titleCase(severity)}</Badge>;
}

export function Empty({ title, children, action }) {
  return (
    <div className="empty">
      <IconInbox width={40} height={40} />
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="loading-block">
      <span className="spinner" />
      {label}
    </div>
  );
}

export function ErrorState({ error, onRetry }) {
  return (
    <div className="empty">
      <IconWarning width={40} height={40} />
      <h3>Something went wrong</h3>
      <p>{error?.message ?? "The request could not be completed."}</p>
      {onRetry && (
        <button className="btn btn-secondary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Modal({ title, hint, onClose, children, footer, wide }) {
  // Escape closes the dialog. Without it a keyboard user has to tab to the
  // close button through the whole form.
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? " modal-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-header">
          <div>
            <h2>{title}</h2>
            {hint && <p className="hint">{hint}</p>}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            <IconX />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export function DefinitionList({ items }) {
  return (
    <dl className="definition-list">
      {items
        .filter((i) => i)
        .map(([term, value]) => (
          <div className="definition-row" key={term}>
            <dt>{term}</dt>
            <dd>{value ?? <span className="hint">Not set</span>}</dd>
          </div>
        ))}
    </dl>
  );
}

/** Steps renders the progress indicator for a staged workflow. */
export function Steps({ steps, current }) {
  return (
    <div className="steps">
      {steps.map((label, i) => (
        <>
          {i > 0 && <span className="step-line" key={`line-${label}`} />}
          <div
            key={label}
            className={`step${i === current ? " active" : ""}${i < current ? " done" : ""}`}
          >
            <span className="step-index">{i < current ? "✓" : i + 1}</span>
            {label}
          </div>
        </>
      ))}
    </div>
  );
}

/* ---------------- helpers ---------------- */

export function titleCase(value) {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatRelative(value) {
  if (!value) return "Never";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "Never";
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  return `${Math.floor(seconds / 86400)} d ago`;
}

export function formatNumber(value, digits = 2) {
  if (value === null || value === undefined) return "-";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

/** CopyButton puts a one-time secret on the clipboard. */
export function CopyButton({ value, label = "Copy" }) {
  const toast = useToast();
  return (
    <button
      className="btn btn-secondary btn-sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          toast.success("Copied to clipboard");
        } catch {
          toast.error("Could not copy", "Select the value and copy it manually.");
        }
      }}
    >
      {label}
    </button>
  );
}
